import React, { useState } from "react"
import { t } from "i18next";

import { ISpeakers, ISpeakersData } from "../../../interfaces/event/speakers"

import BaseToggle from "../../shared/BaseToggle";
import PageTitle from "../../shared/pageTitle/PageTitle";
import Typography from "../../shared/typography/Typography";
import { InputDate } from "../../shared-v2/InputDate";
import Button from "../../shared/button/Button";
import Input from "../../shared/input/Input";

import { IFile, UploadImage } from "../../shared-v2/UploadImage";
import { Card } from "../../shared-v2/Card";
import { Subtitle } from "../../shared-v2/Subtitle";

import { breakpointSize } from "../../../theme/mediaQuerie";
import { Column, InputGroup, Row, RowGrid, ToggleContainer, CardContent, List } from "./style";


interface IProps {
  speakers: ISpeakers;
  setSpeakers: React.Dispatch<React.SetStateAction<ISpeakers>>;
}

const isMobile = window.innerWidth <= breakpointSize.Sm;

export const Speakers = ({setSpeakers, speakers}: IProps) => {
  const [newSpeaker, setNewSpeaker] = useState<ISpeakersData>({
    date: new Date().toISOString().split('T')[0],
    desc: "",
    name: "",
    photo: ""
  });
  const [file, setFile] = useState<IFile | null>(null);
  
  const updateSpeakers = (attr: keyof ISpeakers, value: boolean) => {
    setSpeakers(prev => ({...prev, [attr]: value}));
  }

  const updateSpeaker = (attr: keyof ISpeakersData, value: string) => {
    setNewSpeaker(prev => ({...prev, [attr]: value}));
  }

  const handleAddSpeaker = () => {
    let newItens = speakers.itens;
    newItens.push(newSpeaker);
    setSpeakers(prev => ({...prev, itens: newItens}));
    setNewSpeaker({
      date: new Date().toISOString().split('T')[0],
      desc: "",
      name: "",
      photo: ""
    });
    setFile(null);
  }
  
  const handleRemoveSpeaker = (index: number) => {
    let newItens = speakers.itens;
    newItens.splice(index, 1);
    setSpeakers(prev => ({...prev, itens: newItens}));
  }

  return (
    <>
      <Row>
        { !isMobile && <PageTitle style={{margin: 0}}>{t("dashboard.event.speakers.title")}</PageTitle> }
        <ToggleContainer>
          <Typography type="paragraphS" color="title">{t("dashboard.event.displaySection")}</Typography>
          <BaseToggle
            initialCheck={speakers.menu ?? false} 
            onChange={(checked: boolean) => updateSpeakers("menu", checked)} 
          />
        </ToggleContainer>
      </Row>
      <RowGrid>
        <Column>
          <InputGroup>
            <Subtitle>{t("name")}</Subtitle>
            <Input 
              value={newSpeaker.name} 
              placeholder={t("name")!}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSpeaker("name", e.target.value)} 
            />
          </InputGroup>

          <InputGroup>
            <Subtitle>{t("description")}</Subtitle>
            <Input 
              placeholder={t("description")!}
              value={newSpeaker.desc} 
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSpeaker("desc", e.target.value)} 
            />
          </InputGroup>
          
          <InputGroup>
            <Subtitle>{t("dashboard.event.speakers.date")}</Subtitle>
            <InputDate date={newSpeaker.date} setDate={(value) => updateSpeaker("date", value)} />
          </InputGroup>

          <UploadImage 
            file={file} 
            setFile={(file) => {
              let newState = {...newSpeaker, 'photo': file?.url ?? ""};
              setNewSpeaker(newState);
              setFile(file);
            }} 
            id="speakers-upload-image" 
            maxImageSize={1024 * 1024 * 10} 
            title={t("image")} 
          />

          <Button
            onClick={handleAddSpeaker} 
            size="small"
            style={{padding: "8px 64px"}}
            variant="primary"
            typographyType="paragraphXXS"
            enable={newSpeaker.name.trim() !== ""}
            fullWidth={isMobile}
            tooltip={newSpeaker.name.trim() !== "" ? "" : t("dashboard.event.speakers.tooltip")!}
          >
            {t("add")}
          </Button>
        </Column>
        <List>
          {speakers.itens.length > 0 ? (
            speakers.itens.map((item: ISpeakersData, index: number) => (
            <Card key={item.name} onDelete={() => handleRemoveSpeaker(index)} title={item.name}> 
              <CardContent>
                <div>
                  <Typography type="headingXXS">{t("description") + ": "}</Typography>
                  <Typography type="paragraphXS">{item.desc}</Typography>
                </div>
                <div>
                  <Typography type="headingXXS">{t("date") + ": "}</Typography>
                  <Typography type="paragraphXS">{item.date.replaceAll("-", "/")}</Typography>
                </div>
              </CardContent>
            </Card>
          ))) : (
            <Typography type="paragraphS" color="label">{t("dashboard.event.speakers.noSpeakers")}</Typography>
          )}
        </List>
      </RowGrid>
    </>
  );
}